/**
 * Portal Case Detail - World Class Minimalist Design
 * Apple meets ChatGPT meets Lexy
 */
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, MessageSquare, FileText, Send,
  Loader2, Download, User, Paperclip, CheckCircle,
  Clock, ChevronDown
} from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

interface Message {
  id: string;
  sender_type: 'CLIENT' | 'LAWYER';
  sender_name: string;
  subject?: string;
  body: string;
  created_at: string;
  is_read: boolean;
  read_at?: string;
}

interface Document {
  id: string;
  name: string;
  type?: string;
  uploaded_at: string;
}

const PortalCaseDetail: React.FC = () => {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  const [loading, setLoading] = useState(true);
  const [caseData, setCaseData] = useState<any>(null);
  const [lawyer, setLawyer] = useState<any>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [activeTab, setActiveTab] = useState<'chat' | 'documents'>('chat');
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [lastMessageCount, setLastMessageCount] = useState(0);
  
  // Auto-refresh messages every 5 seconds
  useEffect(() => {
    fetchCaseData();
    
    const pollInterval = setInterval(() => {
      fetchMessagesOnly();
    }, 5000);
    
    return () => clearInterval(pollInterval);
  }, [caseId]);
  
  useEffect(() => {
    // Scroll to bottom when new messages arrive
    if (messages.length > lastMessageCount) {
      scrollToBottom();
      setLastMessageCount(messages.length);
    }
  }, [messages.length, lastMessageCount]);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  // Fetch only messages (lightweight polling)
  const fetchMessagesOnly = async () => {
    try {
      const token = localStorage.getItem('portal_token');
      const response = await axios.get(`${API}/api/portal/my/cases/${caseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const newMessages = (response.data.messages || []).reverse();
      
      // Only update if messages changed
      if (JSON.stringify(newMessages.map((m: Message) => m.id)) !== JSON.stringify(messages.map(m => m.id))) {
        setMessages(newMessages);
        // Mark messages as read
        markMessagesAsRead();
      }
    } catch (error) {
      // Silently fail on polling errors
      console.debug('Polling error:', error);
    }
  };
  
  // Mark lawyer messages as read when client views them
  const markMessagesAsRead = async () => {
    try {
      const token = localStorage.getItem('portal_token');
      await axios.post(
        `${API}/api/portal/my/cases/${caseId}/mark-read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.debug('Failed to mark as read:', error);
    }
  };
  
  const fetchCaseData = async () => {
    try {
      const token = localStorage.getItem('portal_token');
      const response = await axios.get(`${API}/api/portal/my/cases/${caseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCaseData(response.data.case);
      setLawyer(response.data.lawyer);
      setMessages((response.data.messages || []).reverse());
      setDocuments(response.data.documents || []);
    } catch (error) {
      console.error('Failed to fetch case:', error);
      toast.error('Could not load case');
    } finally {
      setLoading(false);
    }
  };
  
  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    
    const messageText = newMessage;
    setNewMessage('');
    setSending(true);
    
    // Optimistic update
    const client = JSON.parse(localStorage.getItem('portal_client') || '{}');
    const optimisticMessage: Message = {
      id: Date.now().toString(),
      sender_type: 'CLIENT',
      sender_name: client.name || 'You',
      body: messageText,
      created_at: new Date().toISOString(),
      is_read: true
    };
    setMessages([...messages, optimisticMessage]);
    
    try {
      const token = localStorage.getItem('portal_token');
      await axios.post(
        `${API}/api/portal/my/cases/${caseId}/messages?body=${encodeURIComponent(messageText)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      toast.error('Could not send message');
      setMessages(messages.filter(m => m.id !== optimisticMessage.id));
      setNewMessage(messageText);
    } finally {
      setSending(false);
    }
  };
  
  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'IN_PROGRESS': return { bg: 'bg-blue-500', text: 'text-blue-600', label: 'In Progress' };
      case 'WAITING_CLIENT': return { bg: 'bg-amber-500', text: 'text-amber-600', label: 'Awaiting Response' };
      case 'COMPLETED': return { bg: 'bg-emerald-500', text: 'text-emerald-600', label: 'Completed' };
      case 'PENDING': return { bg: 'bg-gray-400', text: 'text-gray-500', label: 'Pending' };
      default: return { bg: 'bg-gray-400', text: 'text-gray-500', label: status };
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#fafafa] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-300" />
      </div>
    );
  }
  
  if (!caseData) {
    return (
      <div className="min-h-screen bg-[#fafafa] flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Case not found</p>
          <button
            onClick={() => navigate('/portal/dashboard')}
            className="text-violet-600 hover:text-violet-700 font-medium"
          >
            Return to dashboard
          </button>
        </div>
      </div>
    );
  }
  
  const statusStyle = getStatusStyle(caseData.status);
  
  return (
    <div className="min-h-screen bg-[#fafafa] flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/portal/dashboard')}
              className="p-2 -ml-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-all"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="font-semibold text-gray-900 truncate">{caseData.title}</h1>
              <div className="flex items-center gap-2 mt-0.5">
                <div className={`w-2 h-2 rounded-full ${statusStyle.bg}`} />
                <span className={`text-sm ${statusStyle.text}`}>{statusStyle.label}</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="max-w-3xl mx-auto px-6">
          <div className="flex gap-1">
            {[
              { id: 'chat', label: 'Messages', icon: MessageSquare, count: messages.length },
              { id: 'documents', label: 'Documents', icon: FileText, count: documents.length },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all ${
                  activeTab === tab.id
                    ? 'border-gray-900 text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                  activeTab === tab.id ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-500'
                }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>
      </header>
      
      {/* Main content */}
      <main className="flex-1 flex flex-col max-w-3xl mx-auto w-full">
        <AnimatePresence mode="wait">
          {activeTab === 'chat' && (
            <motion.div
              key="chat"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex-1 flex flex-col"
            >
              {/* Lawyer card */}
              <div className="px-6 py-4">
                <div className="bg-white rounded-2xl p-4 border border-gray-100 flex items-center gap-4">
                  {lawyer?.profile_picture ? (
                    <img src={lawyer.profile_picture} alt={lawyer.name} className="w-12 h-12 rounded-full object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center">
                      <span className="text-white font-semibold">
                        {lawyer?.name?.charAt(0) || 'L'}
                      </span>
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{lawyer?.name || 'Your Lawyer'}</p>
                    <p className="text-sm text-gray-500">Your assigned lawyer</p>
                  </div>
                </div>
              </div>
              
              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                {messages.length === 0 ? (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gray-50 flex items-center justify-center">
                        <MessageSquare className="w-8 h-8 text-gray-300" />
                      </div>
                      <p className="text-gray-500">No messages yet</p>
                      <p className="text-sm text-gray-400 mt-1">Start the conversation below</p>
                    </div>
                  </div>
                ) : (
                  <>
                    {messages.map((msg, index) => {
                      const isClient = msg.sender_type === 'CLIENT';
                      const showAvatar = index === 0 || messages[index - 1]?.sender_type !== msg.sender_type;
                      
                      return (
                        <motion.div
                          key={msg.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`flex gap-3 ${isClient ? 'flex-row-reverse' : ''}`}
                        >
                          {showAvatar ? (
                            !isClient && lawyer?.profile_picture ? (
                              <img 
                                src={lawyer.profile_picture} 
                                alt={lawyer.name} 
                                className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                              />
                            ) : (
                              <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center ${
                                isClient 
                                  ? 'bg-gradient-to-br from-gray-700 to-gray-900'
                                  : 'bg-gradient-to-br from-violet-500 to-indigo-500'
                              }`}>
                                <span className="text-white text-xs font-medium">
                                  {msg.sender_name.charAt(0).toUpperCase()}
                                </span>
                              </div>
                            )
                          ) : (
                            <div className="w-8" />
                          )}
                          
                          <div className={`max-w-[75%] ${isClient ? 'items-end' : 'items-start'}`}>
                            {showAvatar && (
                              <p className={`text-xs text-gray-400 mb-1 ${isClient ? 'text-right' : ''}`}>
                                {msg.sender_name}
                              </p>
                            )}
                            <div className={`rounded-2xl px-4 py-3 ${
                              isClient
                                ? 'bg-gray-900 text-white rounded-tr-md'
                                : 'bg-white border border-gray-100 text-gray-900 rounded-tl-md'
                            }`}>
                              <p className="text-sm whitespace-pre-wrap">{msg.body}</p>
                            </div>
                            <div className={`flex items-center gap-1.5 mt-1 ${isClient ? 'justify-end' : ''}`}>
                              <p className="text-xs text-gray-400">
                                {format(new Date(msg.created_at), 'MMM d, h:mm a')}
                              </p>
                              {/* Read receipt for client messages */}
                              {isClient && (
                                <span className={`flex items-center gap-0.5 ${msg.is_read ? 'text-blue-500' : 'text-gray-300'}`}>
                                  <CheckCircle className="w-3 h-3" />
                                  {msg.is_read && <CheckCircle className="w-3 h-3 -ml-1.5" />}
                                </span>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>
              
              {/* Compose */}
              <div className="sticky bottom-0 bg-white border-t border-gray-100 p-4">
                <div className="max-w-3xl mx-auto flex gap-3">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message..."
                      className="w-full h-12 pl-4 pr-12 bg-gray-50 rounded-2xl border-0 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 transition-all"
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                    />
                  </div>
                  <button
                    onClick={sendMessage}
                    disabled={!newMessage.trim() || sending}
                    className="h-12 w-12 bg-gray-900 hover:bg-gray-800 disabled:bg-gray-200 text-white disabled:text-gray-400 rounded-2xl flex items-center justify-center transition-all"
                  >
                    {sending ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          )}
          
          {activeTab === 'documents' && (
            <motion.div
              key="documents"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="p-6"
            >
              {documents.length === 0 ? (
                <div className="bg-white rounded-2xl p-12 text-center border border-gray-100">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gray-50 flex items-center justify-center">
                    <FileText className="w-8 h-8 text-gray-300" />
                  </div>
                  <p className="text-gray-500">No documents shared yet</p>
                  <p className="text-sm text-gray-400 mt-1">Documents will appear here when shared</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {documents.map((doc) => (
                    <div
                      key={doc.id}
                      className="bg-white rounded-2xl p-4 border border-gray-100 hover:border-gray-200 hover:shadow-lg hover:shadow-gray-100 transition-all flex items-center gap-4 cursor-pointer group"
                    >
                      <div className="w-12 h-12 rounded-xl bg-gray-50 flex items-center justify-center">
                        <FileText className="w-6 h-6 text-gray-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{doc.name}</p>
                        <p className="text-sm text-gray-400">
                          {format(new Date(doc.uploaded_at), 'MMM d, yyyy')}
                        </p>
                      </div>
                      <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-all opacity-0 group-hover:opacity-100">
                        <Download className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default PortalCaseDetail;
